/**
 * Route configuration object for programmatic access
 * Useful for navigation, link generation, and testing
 */
export const routeConfig = {
  withdraw: '/',
  withdrawIframe: '/withdraw-iframe',
  iframe: '/iframe',
  authCallback: '/callback',
  withdrawalConfirmation: '/withdrawal-confirmation',
  error: '/error'
} as const;

/**
 * Navigation service for programmatic navigation
 * Provides type-safe navigation utilities
 */
export class NavigationService {
  /**
   * Get route path by key
   */
  static getRoute(key: RouteKey): RoutePath {
    return routeConfig[key];
  }

  /**
   * Generate URL with query parameters
   */
  static buildUrl(route: RoutePath, params?: Record<string, string>): string {
    if (!params) return route;
    
    const searchParams = new URLSearchParams(params);
    return `${route}?${searchParams.toString()}`;
  }

  /**
   * Check if current path matches route
   */
  static isCurrentRoute(route: RoutePath, currentPath: string): boolean {
    return currentPath === route;
  }

  /**
   * Navigate programmatically (for use with useNavigate hook)
   */
  static createNavigator(navigate: (path: string) => void) {
    return {
      toWithdraw: () => navigate(routeConfig.withdraw),
      toWithdrawIframe: () => navigate(routeConfig.withdrawIframe),
      toIframe: () => navigate(routeConfig.iframe),
      to: (route: RoutePath) => navigate(route),
    };
  }
}

/**
 * Route constants for easy access
 */
export const ROUTES = routeConfig;

/**
 * Common navigation patterns
 */
export const navigationPatterns = {
  /**
   * Navigate to withdrawal page based on mode
   */
  toWithdrawalMode: (mode: 'standalone' | 'iframe') => {
    return mode === 'standalone' ? ROUTES.withdraw : ROUTES.iframe;
  },
} as const;

/**
 * Type definitions for route configuration
 */
export type RouteKey = keyof typeof routeConfig;
export type RoutePath = typeof routeConfig[RouteKey];